// assets/tmf-delivery-eta.js - TMF (port of lnd-delivery-eta.js) · Delivery estimate (business days, counted in the browser)
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Adds n business days (Mon–Fri) to a date. n = 0 returns the same calendar day.
 * @param {Date} date
 * @param {number} n
 * @returns {Date}
 */
export function addBizDays(date, n) {
  const x = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  let count = 0;
  while (count < n) {
    x.setDate(x.getDate() + 1);
    const day = x.getDay();
    if (day !== 0 && day !== 6) count++;
  }
  return x;
}

/**
 * @param {Date} date
 * @returns {string} e.g. "Oct 14"
 */
export function fmtDate(date) {
  return `${MONTHS[date.getMonth()]} ${date.getDate()}`;
}

/**
 * @param {Date} a
 * @param {Date} b
 * @returns {string} e.g. "Oct 21–23" or "Oct 30–Nov 10"
 */
export function fmtRange(a, b) {
  if (a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()) {
    if (a.getDate() === b.getDate()) return fmtDate(a);
    return `${MONTHS[a.getMonth()]} ${a.getDate()}–${b.getDate()}`;
  }
  return `${fmtDate(a)}–${fmtDate(b)}`;
}

/**
 * dispatch = today + processing (min..max) business days,
 * arrive = dispatch + shipping (min..max) business days.
 * @param {Date} today
 * @param {{ processingMin: number, processingMax: number, shippingMin: number, shippingMax: number }} days
 */
export function deliveryDates(today, { processingMin, processingMax, shippingMin, shippingMax }) {
  const d1 = addBizDays(today, processingMin);
  const d2 = addBizDays(today, Math.max(processingMin, processingMax));
  const a1 = addBizDays(d1, shippingMin);
  const a2 = addBizDays(d2, Math.max(shippingMin, shippingMax));
  return {
    todayText: fmtDate(today),
    dispatchText: fmtRange(d1, d2),
    arriveText: fmtRange(a1, a2),
  };
}

/**
 * @typedef {object} TmfDeliveryEtaRefs
 * @property {HTMLElement} today
 * @property {HTMLElement} dispatch
 * @property {HTMLElement} arrive
 */

/**
 * @extends {Component<TmfDeliveryEtaRefs>}
 */
class TmfDeliveryEtaComponent extends Component {
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#section = null;
  }

  /**
   * Size changed: copy the making days of the re-rendered block (large sizes take longer), then redraw dates.
   * Same pattern as assets/lnd-size-note.js (Little Nest).
   * @param {Event & { promise?: Promise<any> }} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (!event.promise) return;
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        const fresh = detail.html.querySelector(
          `tmf-delivery-eta-component[data-block-id="${this.dataset.blockId}"]`
        );
        if (!(fresh instanceof HTMLElement)) return;
        for (const key of ['processingMin', 'processingMax', 'shippingMin', 'shippingMax']) {
          const value = fresh.dataset[key];
          if (value !== undefined) this.dataset[key] = value;
        }
        this.render();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[tmf-delivery-eta] Event promise rejected:', error);
      });
  };

  updatedCallback() {
    super.updatedCallback();
    this.render();
  }

  render() {
    const num = (/** @type {string | undefined} */ value, /** @type {number} */ fallback) => {
      const n = Number.parseInt(value ?? '', 10);
      return Number.isFinite(n) && n >= 0 ? n : fallback;
    };
    const dates = deliveryDates(new Date(), {
      processingMin: num(this.dataset.processingMin, 5),
      processingMax: num(this.dataset.processingMax, 7),
      shippingMin: num(this.dataset.shippingMin, 7),
      shippingMax: num(this.dataset.shippingMax, 12),
    });
    const { today, dispatch, arrive } = this.refs;
    if (today) today.textContent = dates.todayText;
    if (dispatch) dispatch.textContent = dates.dispatchText;
    if (arrive) arrive.textContent = dates.arriveText;
    // TMF: holiday "order by" line hides itself after its cutoff (cached pages); the server hides it too.
    const now = Date.now() / 1000;
    for (const line of this.querySelectorAll('[data-tmf-until]')) {
      if (now > Number(/** @type {HTMLElement} */ (line).dataset.tmfUntil)) line.remove();
    }
  }
}

if (!customElements.get('tmf-delivery-eta-component')) {
  customElements.define('tmf-delivery-eta-component', TmfDeliveryEtaComponent);
}
